import { useCallback } from "react";
import { usePersistedState } from "@/lib/persistedState";

/**
 * A named set of list filters a user can switch back to. Generic over the
 * page's own filter shape: the Prospects and Emails lists store different
 * dropdowns, and each supplies its own reviver for the payload.
 */
export type FilterPreset<T> = {
  id: string;
  name: string;
  filters: T;
};

/** Upper bound on presets per list — a dropdown, not a database. */
export const PRESET_MAX = 20;
/** Upper bound on a preset name, so the dropdown stays readable. */
export const PRESET_NAME_MAX = 40;

function normaliseName(name: string): string {
  return name.trim().slice(0, PRESET_NAME_MAX);
}

function sameName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/**
 * Builds the reviver for a stored preset list from the page's filter reviver.
 * Every entry is validated on its own: a malformed one is dropped rather than
 * poisoning the list, ids are de-duplicated (they are React keys), names are
 * normalised, and both bounds are enforced even on a hand-edited payload.
 */
export function revivePresets<T>(
  reviveFilters: (stored: unknown) => T | null,
): (stored: unknown) => FilterPreset<T>[] | null {
  return (stored) => {
    if (!Array.isArray(stored)) return null;
    const seen = new Set<string>();
    const out: FilterPreset<T>[] = [];
    for (const entry of stored) {
      if (typeof entry !== "object" || entry === null) continue;
      const raw = entry as Record<string, unknown>;
      if (typeof raw.id !== "string" || raw.id === "" || seen.has(raw.id)) continue;
      if (typeof raw.name !== "string") continue;
      const name = normaliseName(raw.name);
      if (name === "") continue;
      const filters = reviveFilters(raw.filters);
      if (filters === null) continue;
      seen.add(raw.id);
      out.push({ id: raw.id, name, filters });
      if (out.length === PRESET_MAX) break;
    }
    return out;
  };
}

/** Shallow equality over the filter shape — every key must match. */
export function sameFilters<T extends Record<string, unknown>>(a: T, b: T): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of keys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

/**
 * Saves {@code filters} under {@code name}. A preset with the same name
 * (case-insensitive, trimmed) is replaced in place, keeping its id and its
 * original spelling; a new name is appended. Returns the input list untouched
 * when the name is empty or the list is full, so callers can detect a refused
 * save by identity.
 */
export function upsertPreset<T>(
  presets: FilterPreset<T>[],
  name: string,
  filters: T,
): FilterPreset<T>[] {
  const clean = normaliseName(name);
  if (clean === "") return presets;
  const index = presets.findIndex((p) => sameName(p.name, clean));
  if (index >= 0) {
    return presets.map((p, i) => (i === index ? { ...p, filters } : p));
  }
  if (presets.length >= PRESET_MAX) return presets;
  return [...presets, { id: crypto.randomUUID(), name: clean, filters }];
}

/**
 * The preset list for one page, persisted under {@code <storageKey>:presets}
 * next to that page's remembered filters.
 */
export function useFilterPresets<T>(
  storageKey: string,
  reviveFilters: (stored: unknown) => T | null,
) {
  const [presets, setPresets] = usePersistedState<FilterPreset<T>[]>(
    `${storageKey}:presets`,
    [],
    revivePresets(reviveFilters),
  );

  /** Returns false when the save was refused (empty name or list full). */
  const save = useCallback(
    (name: string, filters: T): boolean => {
      let accepted = false;
      setPresets((prev) => {
        const next = upsertPreset(prev, name, filters);
        accepted = next !== prev;
        return next;
      });
      return accepted;
    },
    [setPresets],
  );

  const remove = useCallback(
    (id: string) => setPresets((prev) => prev.filter((p) => p.id !== id)),
    [setPresets],
  );

  return { presets, save, remove };
}
